import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Verificador público de PDFs SGSSO.
 *
 * Extrae el SHA-256 embebido en el metadata del PDF (título/keywords/subject)
 * y lo coteja contra la colección `hse_report_signatures`. Sin auth requerida
 * — cualquier auditor externo puede verificar la autenticidad de un reporte.
 */
export default function VerifyPdf() {
  const { t, lang, switchLang } = useLang();
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      toast.error(t("verifyErrNotPdf"));
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/api/hse/verify`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      setResult({ ...data, _file: file.name });
    } catch (err) {
      const msg = err?.response?.data?.detail || t("verifyErrGeneric");
      toast.error(msg);
      setResult({ ok: false, reason: "error", message: msg, _file: file.name });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/login" data-testid="verify-back-link" className="flex items-center gap-3 text-sm hover:text-amber-300">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
            <div>
              <div className="font-black tracking-tight">SGSSO · {t("verifyPdfTitle")}</div>
              <div className="text-[10px] uppercase tracking-widest text-blue-200">UG Managment · by OOMINING</div>
            </div>
          </Link>
          <div className="flex gap-1 bg-white/10 rounded-md p-1">
            <button data-testid="verify-lang-es" onClick={() => switchLang("es")} className={`text-xs font-bold px-2 py-1 rounded ${lang === "es" ? "bg-white text-blue-900" : "text-white/80"}`}>ES</button>
            <button data-testid="verify-lang-en" onClick={() => switchLang("en")} className={`text-xs font-bold px-2 py-1 rounded ${lang === "en" ? "bg-white text-blue-900" : "text-white/80"}`}>EN</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-heading font-black text-slate-900">{t("verifyPdfHeading")}</h2>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">{t("verifyPdfBlurb")}</p>

        <div
          data-testid="verify-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`mt-8 border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
            dragging ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white hover:border-blue-400"
          }`}
        >
          <input
            ref={inputRef}
            data-testid="verify-file-input"
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <div className="mx-auto h-14 w-14 rounded-full bg-blue-900 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          </div>
          <p className="font-semibold text-slate-900">{t("verifyDropHere")}</p>
          <p className="text-xs text-slate-500 mt-1">{t("verifyClickToBrowse")}</p>
          {loading && <p data-testid="verify-loading" className="text-xs text-blue-700 mt-3 animate-pulse">{t("verifyValidating")}</p>}
        </div>

        {result && (
          <div data-testid="verify-result" className="mt-6">
            {result.ok ? <ResultOk res={result} t={t} /> : <ResultFail res={result} t={t} />}
          </div>
        )}

        <div className="mt-10 text-xs text-slate-500 space-y-1">
          <p>· {t("verifyHow1")}</p>
          <p>· {t("verifyHow2")}</p>
          <p>· {t("verifyHow3")}</p>
        </div>

        <SecurityStatusPanel t={t} />
      </main>
    </div>
  );
}

/**
 * Public "Estado de seguridad" panel — badges shown on the verifier landing
 * page so external auditors and clients see the platform's security posture
 * without needing an account. Purely informational, no data leakage.
 */
function SecurityStatusPanel({ t }) {
  const badges = [
    { icon: "🛡", label: t("secBadgeBcrypt"), sub: t("secBadgeBcryptSub"), color: "border-blue-200 bg-blue-50" },
    { icon: "🔒", label: t("secBadgeJwt"), sub: t("secBadgeJwtSub"), color: "border-blue-200 bg-blue-50" },
    { icon: "🚨", label: t("secBadgeBrute"), sub: t("secBadgeBruteSub"), color: "border-amber-200 bg-amber-50" },
    { icon: "🕓", label: t("secBadgeAudit"), sub: t("secBadgeAuditSub"), color: "border-emerald-200 bg-emerald-50" },
    { icon: "💾", label: t("secBadgeBackup"), sub: t("secBadgeBackupSub"), color: "border-emerald-200 bg-emerald-50" },
    { icon: "✍", label: t("secBadgeSha"), sub: t("secBadgeShaSub"), color: "border-indigo-200 bg-indigo-50" },
    { icon: "🌐", label: t("secBadgeCors"), sub: t("secBadgeCorsSub"), color: "border-slate-200 bg-slate-50" },
    { icon: "📴", label: t("secBadgeOffline"), sub: t("secBadgeOfflineSub"), color: "border-slate-200 bg-slate-50" },
  ];
  return (
    <section data-testid="verify-security-status" className="mt-14 border-t border-slate-200 pt-8">
      <div className="flex items-center gap-2 mb-1">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="text-blue-900"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
        <h3 className="text-lg font-heading font-black text-slate-900">{t("secStatusTitle")}</h3>
      </div>
      <p className="text-xs text-slate-500 max-w-3xl mb-5">{t("secStatusBlurb")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {badges.map((b, i) => (
          <div key={i} data-testid={`sec-badge-${i}`} className={`border rounded-lg p-3 flex items-start gap-3 ${b.color}`}>
            <div className="text-2xl leading-none shrink-0">{b.icon}</div>
            <div className="min-w-0">
              <div className="text-[11px] font-black uppercase tracking-wider text-slate-900">{b.label}</div>
              <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">{b.sub}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-5 text-[10px] text-slate-400 uppercase tracking-wider">
        {t("secStatusFooter")}
      </p>
    </section>
  );
}

function ResultOk({ res, t }) {
  return (
    <div data-testid="verify-ok" className="bg-white border border-emerald-200 rounded-lg overflow-hidden shadow-sm">
      <div className="bg-emerald-600 text-white px-5 py-3 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6 9 17l-5-5"/></svg>
        <span className="font-black text-lg tracking-tight">{t("verifyOkBadge")}</span>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-sm text-slate-700">{res.message}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs mt-4">
          <Field label={t("verifyKind")} value={res.kind?.toUpperCase()} testid="verify-field-kind" />
          <Field label={t("verifyGeneratedAt")} value={res.generated_at} testid="verify-field-genat" />
          <Field label={t("verifyGeneratedBy")} value={res.generated_by || "—"} testid="verify-field-genby" />
          <Field label={t("verifyRecords")} value={String(res.records_count ?? 0)} testid="verify-field-rec" />
          <Field label={t("verifyFrom")} value={res.from_date || "—"} testid="verify-field-from" />
          <Field label={t("verifyTo")} value={res.to_date || "—"} testid="verify-field-to" />
          <Field label={t("verifyCompany")} value={res.company_name || "—"} testid="verify-field-company" />
          <Field label={t("verifyContract")} value={res.contract || "—"} testid="verify-field-contract" />
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest">SHA-256</div>
          <code data-testid="verify-field-hash" className="text-[11px] text-slate-800 break-all block font-mono mt-1 bg-slate-50 p-2 rounded">
            {res.sig_hash}
          </code>
        </div>
      </div>
    </div>
  );
}

function ResultFail({ res, t }) {
  const iconClr = res.reason === "no_signature" ? "bg-slate-500" : "bg-red-600";
  const badgeTxt = res.reason === "no_signature" ? t("verifyNoSigBadge") : t("verifyNotFoundBadge");
  return (
    <div data-testid="verify-fail" className="bg-white border border-red-200 rounded-lg overflow-hidden shadow-sm">
      <div className={`${iconClr} text-white px-5 py-3 flex items-center gap-2`}>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span className="font-black text-lg tracking-tight">{badgeTxt}</span>
      </div>
      <div className="p-5">
        <p className="text-sm text-slate-700">{res.message}</p>
        {res.sig_hash && (
          <div className="mt-3">
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest">SHA-256 {t("verifyFoundInPdf")}</div>
            <code className="text-[11px] text-slate-800 break-all block font-mono mt-1 bg-slate-50 p-2 rounded">{res.sig_hash}</code>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase font-black text-slate-500 tracking-widest">{label}</span>
      <span data-testid={testid} className="text-sm text-slate-800 font-medium">{value || "—"}</span>
    </div>
  );
}
